// ---------------------------------------------------------------------------
// ChartViewGrid -- Chart Orchestrator Component
// ---------------------------------------------------------------------------
// Renders the correct chart type for each visible chartable column:
//   - Filters columns where chartType !== 'none' and key is in visibleColumnKeys
//   - Aggregates data using aggregateColumnData() utility
//   - Renders HorizontalBarChart, DonutChart, or GaugeChart
//   - Responsive grid: grid-cols-1 md:grid-cols-2 xl:grid-cols-3
// ---------------------------------------------------------------------------

import React, { useMemo, useEffect } from 'react';
import type { ColumnConfig, ChartType } from '@/components/v11/discovery/types';
import { aggregateColumnData } from '../utils/aggregateColumnData';
import { HorizontalBarChart } from './HorizontalBarChart';
import { VerticalBarChart } from './VerticalBarChart';
import { DonutChart } from './DonutChart';
import { GaugeChart } from './GaugeChart';
import { KpiBadgeChart } from './KpiBadgeChart';
import { AreaDistributionChart } from './AreaDistributionChart';
import { TreemapChart } from './TreemapChart';
import { V11RadarChart } from './V11RadarChart';
import { HistogramChart } from './HistogramChart';
import { ChartViewLoader } from './ChartViewLoader';
import type { LoadingStep } from './ChartViewLoader';
import { useProgressiveLoading } from '../hooks/useProgressiveLoading';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChartViewGridProps {
  /** Raw data rows */
  readonly data: ReadonlyArray<Record<string, unknown>>;
  /** Column configurations with chartType */
  readonly columns: ReadonlyArray<ColumnConfig>;
  /** Keys of currently visible columns */
  readonly visibleColumnKeys: ReadonlyArray<string>;
  /** Loading state */
  readonly isLoading?: boolean;
  /** Total records found (for loading display) */
  readonly totalRecords?: number;
  /** Additional CSS class */
  readonly className?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_BAR_ITEMS = 5;
const MAX_DONUT_ITEMS = 5;
const GAUGE_MAX_VALUE = 10;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ChartViewGrid: React.FC<ChartViewGridProps> = ({
  data,
  columns,
  visibleColumnKeys,
  isLoading = false,
  totalRecords,
  className = '',
}) => {
  // Filter to chartable visible columns
  const chartableColumns = useMemo(() => {
    const visibleSet = new Set(visibleColumnKeys);

    return columns.filter(col => col.chartType !== 'none' && visibleSet.has(col.key));
  }, [columns, visibleColumnKeys]);

  // Define loading steps based on chartable columns
  const loadingStepsConfig = useMemo<ReadonlyArray<Omit<LoadingStep, 'status'>>>(
    () => [
      { id: 'filter', label: `Filtering ${totalRecords?.toLocaleString() || '...'} records...` },
      ...chartableColumns.map((col, index) => ({
        id: col.key,
        label: `Aggregating ${col.label.toLowerCase()}...`,
      })),
      { id: 'render', label: 'Rendering charts...' },
    ],
    [chartableColumns, totalRecords]
  );

  const {
    steps,
    currentStepIndex,
    isLoading: isProgressLoading,
    startLoading,
    completeCurrentStep,
    reset,
  } = useProgressiveLoading({
    steps: loadingStepsConfig,
    onComplete: () => {
      // Optional: callback when all done
    },
  });

  // Start loading when isLoading prop changes to true
  useEffect(() => {
    if (isLoading && !isProgressLoading) {
      startLoading();
    } else if (!isLoading && isProgressLoading) {
      reset();
    }
  }, [isLoading, isProgressLoading, startLoading, reset]);

  // Simulate step completion (in real app, this comes from API responses)
  useEffect(() => {
    if (!isProgressLoading || currentStepIndex < 0) return;

    const timer = setTimeout(
      () => {
        completeCurrentStep(
          currentStepIndex === 0 ? data.length : undefined,
          Math.floor(Math.random() * 300) + 100
        );
      },
      400 + Math.random() * 200
    );

    return () => clearTimeout(timer);
  }, [isProgressLoading, currentStepIndex, completeCurrentStep, data.length]);

  // Show loader while loading
  if (isLoading || isProgressLoading) {
    return (
      <ChartViewLoader
        steps={steps}
        currentStepIndex={currentStepIndex}
        totalCount={totalRecords}
      />
    );
  }

  return (
    <div
      data-testid="chart-view-grid"
      className={`grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 ${className}`}
    >
      {chartableColumns.map((col, chartIndex) => (
        <ChartCell key={col.key} column={col} data={data} colorOffset={chartIndex} />
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ChartCell -- Renders a single chart for a column
// ---------------------------------------------------------------------------

interface ChartCellProps {
  readonly column: ColumnConfig;
  readonly data: ReadonlyArray<Record<string, unknown>> | any; // Can be array or pre-aggregated object
  readonly colorOffset?: number;
}

const ChartCell: React.FC<ChartCellProps> = ({ column, data, colorOffset = 0 }) => {
  // Detect if data is pre-aggregated (object with chart data) or raw array
  const isPreAggregated =
    data && !Array.isArray(data) && typeof data === 'object' && data[column.key];

  const aggregated = useMemo(() => {
    if (isPreAggregated) {
      // Use pre-aggregated data directly (no calculation needed!)
      const chartData = data[column.key] || [];

      // For gauge charts, use totals averages if available
      const average =
        column.key === 'sentiment' && data.totals?.avgSentiment
          ? data.totals.avgSentiment
          : column.key === 'engagement' && data.totals?.avgEngagement
            ? data.totals.avgEngagement
            : 0;

      return {
        type: 'categorical' as const,
        items: chartData,
        average, // Use calculated average for gauges
        min: 0,
        max: 0,
        total: chartData.reduce((sum: number, item: any) => sum + item.value, 0),
      };
    } else {
      // Legacy: aggregate raw data array
      if (!Array.isArray(data)) {
        return { type: 'categorical' as const, items: [], average: 0, min: 0, max: 0, total: 0 };
      }
      return aggregateColumnData(
        data,
        column.key,
        column.chartType,
        column.chartType === 'bar' ? MAX_BAR_ITEMS : MAX_DONUT_ITEMS
      );
    }
  }, [data, column.key, column.chartType, isPreAggregated]);

  // Hide empty charts entirely (no "No data available" cards)
  const isEmpty =
    aggregated.items.length === 0 && aggregated.total === 0 && aggregated.average === 0;
  if (isEmpty) return null;

  const subtitle =
    isPreAggregated && data.totals
      ? `Based on ${data.totals.profiles?.toLocaleString('en-US') || data.totals.posts?.toLocaleString('en-US') || data.totals.comments?.toLocaleString('en-US') || data.totals.brands?.toLocaleString('en-US') || data.totals.teams?.toLocaleString('en-US')} ${data.totals.profiles ? 'profiles' : data.totals.posts ? 'posts' : data.totals.comments ? 'comments' : data.totals.brands ? 'brands' : data.totals.teams ? 'teams' : 'records'}`
      : `Based on ${Array.isArray(data) ? data.length.toLocaleString('en-US') : 0} records`;

  switch (column.chartType) {
    case 'bar':
      return (
        <HorizontalBarChart
          data={aggregated.items}
          title={column.label}
          subtitle={subtitle}
          maxItems={MAX_BAR_ITEMS}
        />
      );

    case 'column':
      return (
        <VerticalBarChart
          data={aggregated.items}
          title={column.label}
          subtitle={subtitle}
          maxItems={MAX_BAR_ITEMS}
          showIcons={column.key === 'platform'}
          preserveOrder={column.key === 'generation'}
        />
      );

    case 'donut':
      return (
        <DonutChart
          data={aggregated.items}
          title={column.label}
          subtitle={subtitle}
          colorOffset={colorOffset}
        />
      );

    case 'gauge': {
      const gaugeValue = Math.round(aggregated.average * 10) / 10;
      const gaugeLabel =
        column.key === 'sentiment'
          ? gaugeValue >= 7
            ? 'Positive'
            : gaugeValue >= 4
              ? 'Neutral'
              : 'Negative'
          : column.key === 'engagement'
            ? gaugeValue >= 3
              ? 'High'
              : gaugeValue >= 1
                ? 'Moderate'
                : 'Low'
            : undefined;
      return (
        <GaugeChart
          value={gaugeValue}
          maxValue={GAUGE_MAX_VALUE}
          title={column.label}
          subtitle={subtitle}
          label={gaugeLabel}
        />
      );
    }

    case 'kpi':
      return <KpiBadgeChart data={aggregated.items} title={column.label} subtitle={subtitle} />;

    case 'area':
      return (
        <AreaDistributionChart
          data={aggregated.items}
          title={column.label}
          subtitle={subtitle}
          colorOffset={colorOffset}
        />
      );

    case 'treemap':
      return (
        <TreemapChart
          data={aggregated.items}
          title={column.label}
          subtitle={subtitle}
          colorOffset={colorOffset}
        />
      );

    case 'radar':
      return (
        <V11RadarChart
          data={aggregated.items}
          title={column.label}
          subtitle={subtitle}
          colorOffset={colorOffset}
        />
      );

    case 'histogram':
      return (
        <HistogramChart
          data={aggregated.items}
          title={column.label}
          subtitle={subtitle}
          colorOffset={colorOffset}
        />
      );

    default:
      return null;
  }
};
